
import React from "react";
import { Heart, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddAllergyButton } from "./AllergyModal";
import { Allergy } from "@/lib/dashboard/allergiesData";
import { useAllergiesState } from "@/lib/dashboard/allergiesData";
import { Skeleton } from "@mui/material";

interface PatientAllergiesProps {
  // allergies: Allergy[];
  isDarkMode: boolean;
  onSaveAllergy: (allergyData: any) => void;
  onEditAllergy: (index: number, allergyData: any) => void;
  onDeleteAllergy: (index: number) => void;
  hasConsultationPermission?: boolean;
}


export function PatientAllergies({
  isDarkMode,
  onSaveAllergy,
  onEditAllergy,
  onDeleteAllergy,
  hasConsultationPermission = false,
}: PatientAllergiesProps) {
  const { DashboardPatientAllergies, patientAllergiesLoading } = useAllergiesState();
  const allergies = DashboardPatientAllergies;
  const canModify = hasConsultationPermission;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-sm font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Allergies
        </h3>
        {canModify && (
          <AddAllergyButton
            onSave={onSaveAllergy}
            isDarkMode={isDarkMode}
          />
        )}
      </div>

      <div className="space-y-2">
        {patientAllergiesLoading ? (
          // Render skeleton placeholders
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 border rounded-xl ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <Skeleton
                variant="circular"
                width={20}
                height={20}
                sx={{
                  bgcolor: isDarkMode ? "grey.800" : "grey.200",
                }}
              />
              <div className="flex-1">
                <Skeleton
                  variant="rounded"
                  height={14}
                  width="60%"
                  sx={{
                    bgcolor: isDarkMode ? "grey.800" : "grey.200",
                    borderRadius: "6px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={12}
                  width="40%"
                  sx={{
                    bgcolor: isDarkMode ? "grey.800" : "grey.200",
                    borderRadius: "6px",
                    mt: 0.5,
                  }}
                />
              </div>
              <Skeleton
                variant="rounded"
                height={20}
                width={60}
                sx={{
                  bgcolor: isDarkMode ? "grey.800" : "grey.200",
                  borderRadius: "10px",
                }}
              />
            </div>
          ))
        ) : allergies.length === 0 ? (
          <div
            className={`text-center py-6 border rounded-xl transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            <Heart className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No allergies recorded</p>
            <p className="text-xs mt-1 opacity-75">
              Click the + button to add allergies
            </p>
          </div>
        ) : (
          allergies.map((allergy, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 border rounded-xl group transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Heart
                className={`w-4 h-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              />
              <div className="flex-1">
                <div
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {allergy.name}
                </div>
                {allergy.reaction && (
                  <div
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {allergy.reaction}
                  </div>
                )}
              </div>
              <div
                className={`text-xs px-2 py-1 rounded-full capitalize ${
                  allergy.severity === "severe"
                    ? isDarkMode
                      ? "bg-red-900/30 text-red-300 border border-red-700"
                      : "bg-red-100 text-red-700"
                    : allergy.severity === "moderate"
                    ? isDarkMode
                      ? "bg-orange-900/30 text-orange-300 border border-orange-700"
                      : "bg-orange-100 text-orange-700"
                    : isDarkMode
                    ? "bg-green-900/30 text-green-300 border border-green-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {allergy.severity}
              </div>
              {canModify && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <AddAllergyButton
                    editMode={{
                      id: allergy.id,
                      allergyName: allergy.name,
                      severity: allergy.severity,
                      reaction: allergy.reaction,
                      notes: allergy.notes,
                    }}
                    onSave={(data) => onEditAllergy(index, data)}
                    trigger={
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Edit2 className="w-3 h-3 text-gray-500" />
                      </Button>
                    }
                    isDarkMode={isDarkMode}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => onDeleteAllergy(index)}
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
